import * as React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiKeys, ApiKeyProvider } from '../state/apikey-state';
import { Spinner } from '../components/ui/spinner';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

/**
 * API Key Management page — generate, list, and revoke keys for a subdomain.
 * Validates: Requirements 7.3, 7.4, 5.5
 * - Display active keys and expiration timestamps (7.3)
 * - Generate and revoke keys (7.4)
 * - Display key value only once at creation (5.5)
 */

/** Inner content component that uses the ApiKey context */
function ApiKeyManagementContent(): React.JSX.Element {
  const { subdomainId } = useParams<{ subdomainId: string }>();
  const { state, loadKeys, generateKey, revokeKey, dismissGeneratedKey } = useApiKeys();

  const [description, setDescription] = React.useState('');
  const [lifetime, setLifetime] = React.useState('P1D');
  const descriptionRef = React.useRef<HTMLInputElement>(null);

  // Load keys on mount
  React.useEffect(() => {
    if (subdomainId) {
      loadKeys(subdomainId);
    }
  }, [subdomainId, loadKeys]);

  // Auto-focus description input
  React.useEffect(() => {
    descriptionRef.current?.focus();
  }, []);

  const handleGenerate = React.useCallback(
    async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      if (!subdomainId) return;
      await generateKey(subdomainId, lifetime, description || undefined);
      setDescription('');
    },
    [subdomainId, lifetime, description, generateKey],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleGenerate(e as unknown as React.FormEvent);
      }
      if (e.key === 'Escape') {
        setDescription('');
        setLifetime('P1D');
      }
    },
    [handleGenerate],
  );

  const handleRevoke = React.useCallback(
    async (keyId: string): Promise<void> => {
      if (!subdomainId) return;
      await revokeKey(subdomainId, keyId);
    },
    [subdomainId, revokeKey],
  );

  // Loading state
  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center py-12" aria-busy="true">
        <Spinner label="Loading API keys..." />
      </div>
    );
  }

  const activeKeys = state.keys.filter((k) => !k.revoked);
  const revokedKeys = state.keys.filter((k) => k.revoked);

  return (
    <section aria-label="API Key Management">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mb-4">
        <ol className="flex items-center gap-1 text-sm text-muted-foreground">
          <li>
            <Link to="/" className="hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded">
              Subdomains
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li className="text-foreground font-medium">API Keys</li>
        </ol>
      </nav>

      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground">API Keys</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage API keys for programmatic DNS access.
        </p>
      </div>

      {/* Error display */}
      {state.error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{state.error}</AlertDescription>
        </Alert>
      )}

      {/* Generated key display — shown only once */}
      {state.generatedKey && (
        <Alert variant="success" className="mb-6">
          <AlertTitle>API Key Generated</AlertTitle>
          <AlertDescription>
            <p className="mb-2">
              Copy this key now. It will not be shown again.
            </p>
            <code className="block rounded bg-muted p-3 font-mono text-sm break-all">
              {state.generatedKey.apiKey}
            </code>
            <p className="mt-2 text-xs">
              Expires: {new Date(state.generatedKey.expiresAt).toLocaleString()}
            </p>
            <details className="mt-3">
              <summary className="cursor-pointer text-sm font-medium text-foreground hover:text-primary">
                Quick start — how to use this key
              </summary>
              <div className="mt-2 space-y-3 text-xs">
                <div>
                  <p className="font-medium text-foreground mb-1">Create an A record:</p>
                  <code className="block rounded bg-muted p-2 font-mono break-all whitespace-pre-wrap">
{`curl -X POST https://ddns.devtechnology.app/dns/records \\
  -H "Authorization: Bearer ${state.generatedKey.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"action":"CREATE","record":{"type":"A","name":"${state.generatedKey.subdomain}","content":"YOUR_IP","ttl":300,"proxied":false}}'`}
                  </code>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">Create a DNS-01 challenge (Let's Encrypt / certbot):</p>
                  <code className="block rounded bg-muted p-2 font-mono break-all whitespace-pre-wrap">
{`curl -X POST https://ddns.devtechnology.app/dns/records \\
  -H "Authorization: Bearer ${state.generatedKey.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"action":"CREATE","record":{"type":"TXT","name":"_acme-challenge.${state.generatedKey.subdomain}","content":"YOUR_CHALLENGE_TOKEN","ttl":60,"proxied":false}}'`}
                  </code>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">Create a CNAME (AWS ACM validation):</p>
                  <code className="block rounded bg-muted p-2 font-mono break-all whitespace-pre-wrap">
{`curl -X POST https://ddns.devtechnology.app/dns/records \\
  -H "Authorization: Bearer ${state.generatedKey.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"action":"CREATE","record":{"type":"CNAME","name":"_validation.${state.generatedKey.subdomain}","content":"TARGET_FROM_ACM","ttl":300,"proxied":false}}'`}
                  </code>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">Delete a record (use Record ID from "My DNS Records" below):</p>
                  <code className="block rounded bg-muted p-2 font-mono break-all whitespace-pre-wrap">
{`curl -X POST https://ddns.devtechnology.app/dns/records \\
  -H "Authorization: Bearer ${state.generatedKey.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"action":"DELETE","recordId":"RECORD_ID_FROM_TABLE","record":{"name":"${state.generatedKey.subdomain}"}}'`}
                  </code>
                </div>
              </div>
            </details>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={dismissGeneratedKey}
            >
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Usage guide (always visible) */}
      <details className="mb-6">
        <summary className="cursor-pointer text-sm font-semibold text-foreground hover:text-primary">
          API Usage Guide
        </summary>
        <Card className="mt-2">
          <CardContent className="p-4 space-y-4 text-sm">
            <div>
              <h4 className="font-medium text-foreground mb-1">Endpoint</h4>
              <code className="block rounded bg-muted p-2 font-mono text-xs">POST https://ddns.devtechnology.app/dns/records</code>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Authentication</h4>
              <p className="text-muted-foreground">Include your API key in the Authorization header:</p>
              <code className="block rounded bg-muted p-2 font-mono text-xs mt-1">Authorization: Bearer ddns_live_...</code>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Actions</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li><code className="font-mono text-xs">CREATE</code> — Create a new DNS record</li>
                <li><code className="font-mono text-xs">UPDATE</code> — Update an existing record (requires <code className="font-mono text-xs">recordId</code>)</li>
                <li><code className="font-mono text-xs">DELETE</code> — Delete a record (requires <code className="font-mono text-xs">recordId</code>)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Record types</h4>
              <p className="text-muted-foreground">A, AAAA, CNAME, TXT, MX, SRV — any record type Cloudflare supports.</p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Scope</h4>
              <p className="text-muted-foreground">Your key can only manage records within your assigned subdomain and its children. For example, if assigned <code className="font-mono text-xs">test.devtechnology.app</code>, you can create:</p>
              <ul className="list-disc list-inside text-muted-foreground mt-1 space-y-0.5">
                <li><code className="font-mono text-xs">test.devtechnology.app</code> (exact match)</li>
                <li><code className="font-mono text-xs">www.test.devtechnology.app</code> (sub-record)</li>
                <li><code className="font-mono text-xs">_acme-challenge.test.devtechnology.app</code> (DNS-01 challenge)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Key lifetime</h4>
              <p className="text-muted-foreground">Keys expire automatically. Use short-lived keys (1–6 hours) for CI/CD and longer keys (90 days – no expiry) for automated cert renewal or persistent services. Revoke keys when no longer needed.</p>
            </div>
          </CardContent>
        </Card>
      </details>

      {/* Copy-pasteable AI-tool reference */}
      <details className="mb-6">
        <summary className="cursor-pointer text-sm font-semibold text-foreground hover:text-primary">
          📋 Copy-pasteable API Reference (for AI tools like Kiro, Copilot, etc.)
        </summary>
        <Card className="mt-2">
          <CardContent className="p-0">
            <pre className="rounded bg-muted p-4 font-mono text-xs overflow-x-auto whitespace-pre-wrap">{`# DelegatedDNS API Reference
# Base URL: https://ddns.devtechnology.app
# Auth: Bearer token in Authorization header (API key from user console)

## Create DNS Record
POST /dns/records
Headers:
  Authorization: Bearer <your_ddns_live_key>
  Content-Type: application/json
Body:
{
  "action": "CREATE",
  "record": {
    "type": "A" | "AAAA" | "CNAME" | "TXT" | "MX" | "SRV",
    "name": "<fqdn within your subdomain scope>",
    "content": "<record value>",
    "ttl": 300,
    "proxied": false
  }
}
Response: { "success": true, "record": { "id": "<cloudflare_record_id>", "type", "name", "content", "ttl", "proxied" } }

## Update DNS Record
POST /dns/records
Body:
{
  "action": "UPDATE",
  "recordId": "<cloudflare_record_id from create response>",
  "record": { "type", "name", "content", "ttl", "proxied" }
}

## Delete DNS Record
POST /dns/records
Body:
{
  "action": "DELETE",
  "recordId": "<record_id_from_my_dns_records_table>",
  "record": { "name": "myapp.devtechnology.app" }
}
Note: Find your Record IDs in the "My DNS Records" table below. Only recordId and record.name are required for deletion.

## Scope Rules
- Your key is scoped to a single subdomain (e.g., myapp.devtechnology.app)
- You can manage: exact match, sub-records (www.myapp...), and _acme-challenge.myapp...
- You cannot modify records outside your subdomain scope

## Common Patterns

### Point subdomain to a server (A record)
{ "action": "CREATE", "record": { "type": "A", "name": "myapp.devtechnology.app", "content": "1.2.3.4", "ttl": 300, "proxied": false } }

### Delete a record (use Record ID from "My DNS Records" table)
{ "action": "DELETE", "recordId": "abc123def456", "record": { "name": "myapp.devtechnology.app" } }

### Let's Encrypt DNS-01 challenge (TXT record)
{ "action": "CREATE", "record": { "type": "TXT", "name": "_acme-challenge.myapp.devtechnology.app", "content": "<certbot_validation_token>", "ttl": 60, "proxied": false } }

### AWS ACM DNS validation (CNAME record)
{ "action": "CREATE", "record": { "type": "CNAME", "name": "_validation-name.myapp.devtechnology.app", "content": "_target-from-acm.acm-validations.aws.", "ttl": 300, "proxied": false } }

### CNAME to an ALB/CloudFront distribution
{ "action": "CREATE", "record": { "type": "CNAME", "name": "myapp.devtechnology.app", "content": "my-alb-123.us-east-1.elb.amazonaws.com", "ttl": 300, "proxied": false } }

## Error Codes
- 400: Validation error (bad payload, missing fields, out-of-scope record name)
- 401: Invalid or expired API key
- 403: Record name outside authorized subdomain scope
- 502: Cloudflare API error (check record format)
`}</pre>
          </CardContent>
        </Card>
      </details>

      {/* Generate key form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Generate New Key</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleGenerate} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label htmlFor="key-description" className="mb-1 block text-sm font-medium text-foreground">
                Description
              </label>
              <Input
                id="key-description"
                ref={descriptionRef}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., ACM validation key"
                aria-describedby="key-description-hint"
              />
              <p id="key-description-hint" className="mt-1 text-xs text-muted-foreground">
                Optional label to identify this key.
              </p>
            </div>
            <div className="w-full sm:w-40">
              <label htmlFor="key-lifetime" className="mb-1 block text-sm font-medium text-foreground">
                Lifetime
              </label>
              <select
                id="key-lifetime"
                value={lifetime}
                onChange={(e) => setLifetime(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="PT1H">1 hour</option>
                <option value="PT6H">6 hours</option>
                <option value="P1D">24 hours (default)</option>
                <option value="P3D">3 days</option>
                <option value="P7D">7 days</option>
                <option value="P90D">90 days</option>
                <option value="P180D">180 days</option>
                <option value="P365D">1 year</option>
                <option value="NONE">No expiry</option>
              </select>
            </div>
            <Button type="submit" loading={state.isGenerating} className="sm:self-end">
              Generate Key
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Active keys list */}
      <div className="mb-6">
        <h3 className="mb-3 text-lg font-medium text-foreground">
          Active Keys ({activeKeys.length})
        </h3>
        {activeKeys.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No active keys. Generate one above to get started.
          </p>
        ) : (
          <div className="space-y-3">
            {activeKeys.map((key) => (
              <div
                key={key.keyId}
                className="flex items-center justify-between rounded-lg border p-4"
              >
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    {key.description || key.keyId}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge variant={isExpiringSoon(key.expiresAt) ? 'warning' : 'success'}>
                      {isExpiringSoon(key.expiresAt) ? 'Expiring soon' : 'Active'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      Expires {new Date(key.expiresAt).toLocaleString()}
                    </span>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleRevoke(key.keyId)}
                  aria-label={`Revoke key ${key.description || key.keyId}`}
                >
                  Revoke
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Revoked keys list */}
      {revokedKeys.length > 0 && (
        <div>
          <h3 className="mb-3 text-lg font-medium text-muted-foreground">
            Revoked Keys ({revokedKeys.length})
          </h3>
          <div className="space-y-3 opacity-60">
            {revokedKeys.map((key) => (
              <div
                key={key.keyId}
                className="flex items-center justify-between rounded-lg border p-4"
              >
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    {key.description || key.keyId}
                  </p>
                  <Badge variant="destructive">Revoked</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DNS Records Section */}
      <DnsRecordsSection subdomainId={subdomainId!} />
    </section>
  );
}

// ─── DNS Records Section ─────────────────────────────────────────────────────

interface DnsRecord {
  readonly id: string;
  readonly type: string;
  readonly name: string;
  readonly content: string;
  readonly ttl: number;
  readonly proxied: boolean;
}

function DnsRecordsSection({ subdomainId }: { subdomainId: string }): React.JSX.Element {
  const [records, setRecords] = React.useState<readonly DnsRecord[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchRecords = React.useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const { get } = await import('../services/api-client');
      const data = await get<{ records: DnsRecord[]; subdomain: string }>(`/user/subdomains/${subdomainId}/dns-records`);
      setRecords(data.records);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load DNS records');
    } finally {
      setIsLoading(false);
    }
  }, [subdomainId]);

  React.useEffect(() => { fetchRecords(); }, [fetchRecords]);

  return (
    <div className="mt-8">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">My DNS Records</h3>
        <Button variant="outline" size="sm" onClick={fetchRecords} disabled={isLoading}>
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <Spinner label="Loading DNS records..." />
      ) : error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : records.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No DNS records found for this subdomain. Use the API to create records.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Content</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">TTL</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Record ID</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id} className="border-b">
                  <td className="px-4 py-3"><Badge variant="secondary">{record.type}</Badge></td>
                  <td className="px-4 py-3 font-mono text-xs">{record.name}</td>
                  <td className="px-4 py-3 font-mono text-xs max-w-[200px] truncate">{record.content}</td>
                  <td className="px-4 py-3">{record.ttl === 1 ? 'Auto' : `${record.ttl}s`}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{record.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 text-xs text-muted-foreground border-t bg-muted/30">
            Use the Record ID with <code className="font-mono">{"\"action\": \"DELETE\", \"recordId\": \"<id>\""}</code> to delete a record via the API.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * API Key Management page wrapper that provides the ApiKeyProvider context.
 */
export function ApiKeyManagementPage(): React.JSX.Element {
  return (
    <ApiKeyProvider>
      <ApiKeyManagementContent />
    </ApiKeyProvider>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isExpiringSoon(expiresAt: string): boolean {
  const expiresMs = new Date(expiresAt).getTime();
  const nowMs = Date.now();
  const oneHourMs = 60 * 60 * 1000;
  return expiresMs - nowMs < oneHourMs;
}
